/**
 * Payment Lock System — Main controller
 * Modular ES6 classes · No external dependencies
 *
 * Classes: ThemeSettings, SessionManager, HashValidator, AnimationManager,
 *          OverlayController, UnlockController, PaymentLock
 */

(function () {
  'use strict';

  const Security = window.PaymentLockSecurity;
  if (!Security) {
    console.warn('[PaymentLock] payment-security.js must load before payment-lock.js');
    return;
  }

  /* ── ThemeSettings ───────────────────────────────────────── */

  class ThemeSettings {
    constructor(root) {
      const el = root || document.getElementById('payment-lock-config');
      this.raw = {};

      if (el?.textContent) {
        try {
          this.raw = JSON.parse(el.textContent);
        } catch (error) {
          console.warn('[PaymentLock] Invalid config JSON:', error);
        }
      }
    }

    get enabled() {
      return Boolean(this.raw.enabled);
    }

    get developerMode() {
      return Boolean(this.raw.developerMode);
    }

    get previewOverlay() {
      return Boolean(this.raw.previewOverlay);
    }

    get passwordHash() {
      return this.raw.viewPasswordHash || this.raw.passwordHash || '';
    }

    get viewPasswordHash() {
      return this.raw.viewPasswordHash || this.raw.passwordHash || '';
    }

    get disablePasswordHash() {
      return this.raw.disablePasswordHash || '';
    }

    get disableRevision() {
      return this.raw.disableRevision || '';
    }

    get unlockToken() {
      return this.raw.unlockToken || '';
    }

    get sessionDuration() {
      return this.raw.sessionDuration || '24h';
    }

    get licenseModeEnabled() {
      return Boolean(this.raw.licenseModeEnabled);
    }

    get licenseApiUrl() {
      return this.raw.licenseApiUrl || '';
    }

    get designMode() {
      return Boolean(this.raw.designMode);
    }

    log(...args) {
      if (this.developerMode) console.log('[PaymentLock]', ...args);
    }

    warn(...args) {
      if (this.developerMode) console.warn('[PaymentLock]', ...args);
    }
  }

  /* ── SessionManager ──────────────────────────────────────── */

  class SessionManager {
    constructor(settings) {
      this.settings = settings;
    }

    isValid() {
      return Security.hasValidSession();
    }

    save() {
      Security.saveSession(this.settings.sessionDuration);
      this.settings.log('Session saved for', this.settings.sessionDuration);
    }

    clear() {
      Security.clearSession();
      Security.clearPermanentDisable();
      this.settings.log('Session and permanent disable cleared');
    }
  }

  /* ── HashValidator ───────────────────────────────────────── */

  class HashValidator {
    constructor(settings) {
      this.settings = settings;
    }

    async validate(password) {
      return Security.resolvePasswordType(password, {
        view: this.settings.viewPasswordHash,
        disable: this.settings.disablePasswordHash,
      });
    }
  }

  /* ── AnimationManager ──────────────────────────────────── */

  class AnimationManager {
    showOverlay(overlay) {
      if (!overlay) return;
      overlay.classList.remove('is-hidden');
      overlay.classList.add('is-visible', 'pl-animate-fade-in');
      overlay.setAttribute('aria-hidden', 'false');
    }

    hideOverlay(overlay) {
      if (!overlay) return;
      overlay.classList.remove('is-visible', 'pl-animate-fade-in');
      overlay.classList.add('is-hidden');
      overlay.setAttribute('aria-hidden', 'true');
    }

    openModal(modal) {
      if (!modal) return;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('payment-lock-modal-open');
    }

    closeModal(modal) {
      if (!modal) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('payment-lock-modal-open');
    }
  }

  /* ── OverlayController ─────────────────────────────────── */

  class OverlayController {
    constructor(settings, animation) {
      this.settings = settings;
      this.animation = animation;
      this.overlay = document.querySelector('[data-payment-lock-overlay]');
      this.countdownEl = document.getElementById('payment-lock-countdown');
      this.countdownInterval = null;
    }

    lockBody() {
      document.documentElement.classList.add('payment-lock-active');
    }

    unlockBody() {
      document.documentElement.classList.remove('payment-lock-active');
    }

    show() {
      this.lockBody();
      this.animation.showOverlay(this.overlay);
      this.startCountdown();
      this.trapFocus();
      requestAnimationFrame(() => {
        this.overlay?.querySelector('#payment-lock-password')?.focus();
      });
    }

    hide() {
      this.animation.hideOverlay(this.overlay);
      this.unlockBody();
      this.stopCountdown();
      this.releaseFocus();
    }

    startCountdown() {
      if (!this.countdownEl) return;

      const target = this.countdownEl.dataset.countdownTarget;
      if (!target) return;

      const targetDate = new Date(target).getTime();
      if (Number.isNaN(targetDate)) return;

      const update = () => {
        const diff = Math.max(0, targetDate - Date.now());
        const days = Math.floor(diff / 86400000);
        const hours = Math.floor((diff % 86400000) / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);

        this.setUnit('days', days);
        this.setUnit('hours', hours);
        this.setUnit('minutes', minutes);
        this.setUnit('seconds', seconds);

        if (diff <= 0) this.stopCountdown();
      };

      update();
      this.countdownInterval = setInterval(update, 1000);
    }

    setUnit(name, value) {
      const el = this.countdownEl?.querySelector(`[data-unit="${name}"]`);
      if (el) el.textContent = String(value).padStart(2, '0');
    }

    stopCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    }

    trapFocus() {
      if (!this.overlay) return;

      this.overlay.addEventListener('keydown', this.handleFocusTrap);
      const focusable = this.overlay.querySelector('.payment-lock-logo');
      focusable?.focus();
    }

    releaseFocus() {
      this.overlay?.removeEventListener('keydown', this.handleFocusTrap);
    }

    handleFocusTrap = (event) => {
      if (event.key !== 'Tab' || !this.overlay) return;

      const focusables = this.overlay.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
  }

  /* ── UnlockController ──────────────────────────────────── */

  class UnlockController {
    constructor(settings, session, validator, animation, overlay) {
      this.settings = settings;
      this.session = session;
      this.validator = validator;
      this.animation = animation;
      this.overlay = overlay;

      this.form = document.querySelector('[data-payment-unlock-form]');
      this.passwordInput = document.getElementById('payment-lock-password');
      this.messageEl = document.querySelector('[data-overlay-message]');
      this.loadingEl = document.querySelector('[data-overlay-loading]');
    }

    bind() {
      this.enforcePasswordMask();
      this.form?.addEventListener('submit', this.handleSubmit);
    }

    enforcePasswordMask() {
      if (!this.passwordInput) return;

      const lockType = () => {
        if (this.passwordInput.getAttribute('type') !== 'password') {
          this.passwordInput.setAttribute('type', 'password');
        }
      };

      lockType();
      this.passwordInput.addEventListener('input', lockType);
      this.passwordInput.addEventListener('focus', lockType);
    }

    checkUrlUnlock() {
      return false;
    }

    setMessage(text, type) {
      if (!this.messageEl) return;
      this.messageEl.textContent = text;
      this.messageEl.classList.remove('is-error', 'is-success');
      if (type) this.messageEl.classList.add(`is-${type}`);
    }

    clearMessage() {
      this.setMessage('', null);
    }

    setLoading(active) {
      this.loadingEl?.classList.toggle('is-active', active);
      if (this.form) {
        this.form.querySelector('button[type="submit"]').disabled = active;
        this.passwordInput.disabled = active;
      }
    }

    handleSubmit = async (event) => {
      event.preventDefault();
      const password = this.passwordInput?.value || '';

      if (!password) {
        this.setMessage('Please enter your access password.', 'error');
        return;
      }

      this.setLoading(true);
      this.clearMessage();

      try {
        const result = await this.validator.validate(password);
        if (result === 'view') {
          this.setMessage('Welcome! Loading store…', 'success');
          setTimeout(() => this.completeUnlock('view'), 500);
        } else if (result === 'disable') {
          this.setMessage('Authorized. Popup permanently disabled on this browser.', 'success');
          setTimeout(() => this.completeUnlock('disable'), 500);
        } else {
          this.setMessage('Incorrect password. Please try again.', 'error');
          this.setLoading(false);
        }
      } catch {
        this.setMessage('Something went wrong. Please try again.', 'error');
        this.setLoading(false);
      }
    };

    completeUnlock(mode) {
      if (mode === 'view') this.session.save();
      if (mode === 'disable') Security.savePermanentDisable(this.settings.disableRevision);
      this.setLoading(false);
      this.overlay.hide();
    }
  }

  /* ── PaymentLock (orchestrator) ────────────────────────── */

  class PaymentLock {
    constructor() {
      this.settings = new ThemeSettings();
      this.session = new SessionManager(this.settings);
      this.validator = new HashValidator(this.settings);
      this.animation = new AnimationManager();
      this.overlay = new OverlayController(this.settings, this.animation);
      this.unlock = new UnlockController(
        this.settings,
        this.session,
        this.validator,
        this.animation,
        this.overlay
      );
    }

    async init() {
      if (!this.settings.enabled) {
        this.settings.log('Payment lock disabled');
        return;
      }

      if (this.settings.designMode) {
        this.settings.log('Theme editor detected — lock skipped');
        return;
      }

      this.unlock.bind();
      this.applyCssVariables();

      if (this.settings.previewOverlay) {
        this.settings.log('Preview mode — showing overlay');
        this.overlay.show();
        return;
      }

      if (Security.isAuthorizedRemoval(this.settings.disableRevision)) {
        this.settings.log('Authorized removal — guard disabled');
        this.overlay.hide();
        return;
      }

      if (this.session.isValid()) {
        this.settings.log('Valid session — overlay hidden');
        this.overlay.hide();
        return;
      }

      if (this.settings.licenseModeEnabled) {
        const licensed = await Security.verifyLicense({
          licenseModeEnabled: true,
          licenseApiUrl: this.settings.licenseApiUrl,
          developerMode: this.settings.developerMode,
        });

        if (licensed) {
          this.settings.log('License verified — overlay hidden');
          this.overlay.hide();
          return;
        }
      }

      this.settings.log('Showing coming soon overlay');
      this.overlay.show();
    }

    applyCssVariables() {
      const root = document.documentElement;
      const vars = this.settings.raw.cssVars || {};
      Object.entries(vars).forEach(([key, value]) => {
        if (value) root.style.setProperty(key, value);
      });
    }
  }

  /* ── Bootstrap ───────────────────────────────────────────── */

  function bootstrap() {
    const lock = new PaymentLock();
    lock.init().catch((error) => {
      console.warn('[PaymentLock] Initialization error:', error);
    });

    window.PaymentLock = lock;
    window.verifyLicense = Security.verifyLicense;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
